/**
 * Reiter „Leistungskatalog“ im Baselining-Panel: der Katalog, auf den jede Rechnungsposition
 * zugeordnet wird — abgeleitet aus GEFMA 200 (Phase 6), mit den Codes der Normen je Knoten und, bei
 * der Technik, je Leistungsart. Lesend; gepflegt wird der Katalog von uns (tri-server
 * docs/fm-baselining-entwurf.md 5.4/5.5).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, RefreshCw, Search } from "lucide-react";

import { authenticationAtom } from "../../State";
import { useBaseliningAPI, type Catalogue, type CatalogueNode } from "../../BaseliningAPI";
import { SCHEME_LABELS, allIds, countNodes, primaryCode, visibleRows } from "./catalogueModel";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

export default function ServiceCatalogueTab() {
  const { t, i18n } = useTranslation();
  const [authentication] = useAtom(authenticationAtom) as any;
  const customer: string = authentication?.customer ?? "";
  const api = useBaseliningAPI(customer);

  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!customer) return;
    setLoading(true);
    setError(undefined);
    try {
      setCatalogue(await api.getCatalogue());
    } catch (e: any) {
      setError(e?.message ?? t("baselining.catalogue.loadFailed"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  useEffect(() => {
    void load();
  }, [load]);

  const roots = catalogue?.nodes ?? [];
  const rows = useMemo(() => visibleRows(roots, expanded, query), [roots, expanded, query]);
  const selected = useMemo(() => {
    const find = (nodes: CatalogueNode[]): CatalogueNode | undefined => {
      for (const n of nodes) {
        if (n.id === selectedId) return n;
        const hit = find(n.children);
        if (hit) return hit;
      }
      return undefined;
    };
    return selectedId ? find(roots) : undefined;
  }, [roots, selectedId]);

  const english = i18n.language?.startsWith("en");
  const label = (node: CatalogueNode) => (english && node.nameEn ? node.nameEn : node.name);

  const toggle = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (loading && !catalogue) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (roots.length === 0) {
    return (
      <Alert>
        <AlertDescription>{t("baselining.catalogue.empty")}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t("baselining.catalogue.intro", { count: countNodes(roots) })}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[16rem] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("baselining.catalogue.searchPlaceholder")}
            className="pl-8"
            aria-label={t("baselining.catalogue.searchPlaceholder")}
          />
        </div>
        <Button variant="outline" size="sm" onClick={() => setExpanded(new Set(allIds(roots)))}>
          <ChevronsUpDown className="mr-1 h-4 w-4" />
          {t("baselining.catalogue.expandAll")}
        </Button>
        <Button variant="outline" size="sm" onClick={() => setExpanded(new Set())}>
          <ChevronsDownUp className="mr-1 h-4 w-4" />
          {t("baselining.catalogue.collapseAll")}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => void load()} aria-label={t("baselining.catalogue.reload")}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="rounded-md border" role="tree" aria-label={t("baselining.tabs.catalogue")}>
          {rows.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">{t("baselining.catalogue.noMatch")}</p>
          ) : (
            rows.map(({ node, depth, hasChildren, expanded: open }) => {
              const gefma = primaryCode(node, "gefma200");
              const active = node.id === selectedId;
              return (
                <div
                  key={node.id}
                  role="treeitem"
                  aria-expanded={hasChildren ? open : undefined}
                  aria-selected={active}
                  className={`flex cursor-pointer items-center gap-2 border-b px-2 py-1.5 text-sm last:border-b-0 hover:bg-muted/50 ${active ? "bg-muted" : ""}`}
                  style={{ paddingLeft: `${0.5 + depth * 1.25}rem` }}
                  onClick={() => setSelectedId(node.id)}
                >
                  <button
                    type="button"
                    className="flex h-5 w-5 shrink-0 items-center justify-center rounded hover:bg-muted"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (hasChildren) toggle(node.id);
                    }}
                    aria-label={open ? t("baselining.catalogue.collapse") : t("baselining.catalogue.expand")}
                    disabled={!hasChildren}
                  >
                    {hasChildren ? open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" /> : null}
                  </button>
                  <span className={`flex-1 truncate ${depth === 0 ? "font-medium" : ""}`}>{label(node)}</span>
                  {node.origin === "tenant" && <Badge variant="secondary">{t("baselining.catalogue.own")}</Badge>}
                  {gefma && (
                    <span className="shrink-0 font-mono text-xs text-muted-foreground">
                      {gefma.activity ? t("baselining.catalogue.perActivity") : gefma.code}
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div>
          {selected ? (
            <CatalogueNodeDetail node={selected} label={label(selected)} />
          ) : (
            <p className="text-sm text-muted-foreground">{t("baselining.catalogue.selectHint")}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function CatalogueNodeDetail({ node, label }: { node: CatalogueNode; label: string }) {
  const { t, i18n } = useTranslation();
  const english = i18n.language?.startsWith("en");
  const activityLabel = (value?: string) =>
    value ? (node.activities.find((a) => a.value === value)?.[english ? "en" : "de"] ?? value) : t("baselining.catalogue.allActivities");

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{label}</CardTitle>
        <p className="font-mono text-xs text-muted-foreground">{node.key}</p>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {node.description && <p>{node.description}</p>}

        <div className="flex flex-wrap gap-2">
          {node.kind && <Badge variant="outline">{t(`baselining.kind.${node.kind}`, node.kind)}</Badge>}
          {node.defaultBudgetType && <Badge variant="outline">{t(`baselining.budgetType.${node.defaultBudgetType}`, node.defaultBudgetType)}</Badge>}
          {node.origin === "tenant" && <Badge variant="secondary">{t("baselining.catalogue.own")}</Badge>}
        </div>

        {node.activities.length > 0 && (
          <div>
            <h4 className="mb-1 font-medium">{t("baselining.catalogue.activities")}</h4>
            <div className="flex flex-wrap gap-1">
              {node.activities.map((a) => (
                <Badge key={a.value} variant="secondary">
                  {english ? a.en : a.de}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {node.quantityBases && node.quantityBases.length > 0 && (
          <div>
            <h4 className="mb-1 font-medium">{t("baselining.catalogue.quantityBases")}</h4>
            <ul className="list-inside list-disc text-muted-foreground">
              {node.quantityBases.map((q) => (
                <li key={q.basis}>
                  {t(`baselining.quantityBasis.${q.basis}`, q.basis)} ({q.unit})
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <h4 className="mb-1 font-medium">{t("baselining.catalogue.crosswalk")}</h4>
          {node.crosswalk.length === 0 ? (
            <p className="text-muted-foreground">{t("baselining.catalogue.noCrosswalk")}</p>
          ) : (
            <table className="w-full text-left text-xs">
              <thead className="text-muted-foreground">
                <tr>
                  <th className="py-1 pr-2 font-normal">{t("baselining.catalogue.scheme")}</th>
                  <th className="py-1 pr-2 font-normal">{t("baselining.catalogue.code")}</th>
                  <th className="py-1 pr-2 font-normal">{t("baselining.catalogue.activity")}</th>
                  <th className="py-1 font-normal">{t("baselining.catalogue.relation")}</th>
                </tr>
              </thead>
              <tbody>
                {node.crosswalk.map((c, i) => (
                  <tr key={`${c.scheme}-${c.code}-${c.activity ?? ""}-${i}`} className="border-t align-top" title={c.evidence}>
                    <td className="py-1 pr-2">
                      {SCHEME_LABELS[c.scheme] ?? c.scheme}
                      {c.schemeVersion ? <span className="text-muted-foreground"> ({c.schemeVersion})</span> : null}
                    </td>
                    <td className="py-1 pr-2">
                      <span className="font-mono">{c.code}</span>
                      {c.codeLabel ? <div className="text-muted-foreground">{c.codeLabel}</div> : null}
                    </td>
                    <td className="py-1 pr-2">{activityLabel(c.activity)}</td>
                    <td className="py-1">{t(`baselining.relation.${c.relation}`, c.relation)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
